if (typeof NewsletterPopupController !== "function") {
  class NewsletterPopupController extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        modalBox: 'modal-box',
        reminderButton: '[data-newsletter-reminder]',
        formSuccess: '[data-form-success]',
        closeButton: '[data-js-close]',
        rewardsButton: '.ba-loy-namespace'
      };

      this.storageKeys = {
        triggered: 'newsletter-popup-triggered',
        subscribed: 'newsletter-popup-subscribed',
        dismissed: 'newsletter-popup-dismissed',
        successShown: 'newsletter-popup-success-shown',
        dismissedAt: 'newsletter-popup-dismissed-at'
      };

      // Popup stays hidden for this long after the visitor closes it without subscribing
      this.dismissCooldownMs = 7 * 24 * 60 * 60 * 1000;

      // Cookie banner (Pandectes) detection
      this.consent = {
        cookieName: '_pandectes_gdpr',
        hostSelector: 'pandectes-cmp',
        pollIntervalMs: 500,
        noBannerGraceMs: 5000,   // Pandectes loaded, banner never opened (e.g. outside EEA/UK/CH) -> proceed
        noCmpTimeoutMs: 8000     // Pandectes not present at all -> proceed
      };
      
      this._handleScroll = this._handleScroll.bind(this);
      this._handleMouseMove = this._handleMouseMove.bind(this);
      this._handleKeydown = this._handleKeydown.bind(this);
      this._handleModalClick = this._handleModalClick.bind(this);
      this._handleReminderClick = this._handleReminderClick.bind(this);
      this._handleCookieConsent = this._handleCookieConsent.bind(this);
      this._handleCloseClick = this._handleCloseClick.bind(this);
    }

    connectedCallback() {
      // If the element is upgraded before its children are parsed, wait for the DOM
      if (!this.querySelector(this.selectors.modalBox) && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });

        return;
      }

      this.reminderButton = document.querySelector(this.selectors.reminderButton);
      this.options = JSON.parse(this.dataset.newsletterOptions || '{}');
      this.modalBox = this.querySelector(this.selectors.modalBox) || this;

      this.state = {
        triggered: sessionStorage.getItem(this.storageKeys.triggered) === 'true',
        dismissed: this.isDismissedRecently(),
        subscribed: localStorage.getItem(this.storageKeys.subscribed) === 'true',
        successShown: localStorage.getItem(this.storageKeys.successShown) === 'true'
      };

      if (this.state.subscribed) {
        this.hideReminderButton();

        if (!this.state.successShown) {
          this.showPopup();
          this.state.successShown = true;
          localStorage.setItem(this.storageKeys.successShown, 'true');
        }
        
        return;
      }

      const hasCustomerPosted = document.location.search.includes('customer_posted');
      const hasNewsletterHash = document.location.hash.includes('popup-newsletter-signup');

      if (this.querySelector(this.selectors.formSuccess) ||
          (hasCustomerPosted && hasNewsletterHash)) {
        this.handleSuccessfulSubscription();
        
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      if (this._timeoutId) {
        clearTimeout(this._timeoutId);
        this._timeoutId = null;
      }
      
      window.removeEventListener('scroll', this._handleScroll);
      document.removeEventListener('mousemove', this._handleMouseMove);
      document.removeEventListener('keydown', this._handleKeydown);
      window.removeEventListener('PandectesEvent_OnConsent', this._handleCookieConsent);
      document.removeEventListener('PandectesEvent_OnConsent', this._handleCookieConsent);
      this.stopConsentWatcher();
      
      if (this.modalBox) {
        this.modalBox.removeEventListener('click', this._handleModalClick);
      }

      if (this.reminderButton) {
        this.reminderButton.removeEventListener('click', this._handleReminderClick);
      }
      
      const closeButtons = this.querySelectorAll(this.selectors.closeButton);
      
      closeButtons.forEach(btn => {
        btn.removeEventListener('click', this._handleCloseClick);
      });
    }
    
    _handleScroll() {
      if (this.state.triggered) {
        window.removeEventListener('scroll', this._handleScroll);
        
        return;
      }

      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;

      if (scrollPercent >= this._scrollTriggerPercent) {
        window.removeEventListener('scroll', this._handleScroll);
        this.triggerPopup('scroll_depth');
      }
    }

    _handleMouseMove(e) {
      if (this.state.triggered) {
        document.removeEventListener('mousemove', this._handleMouseMove);
        
        return;
      }

      const topThreshold = 20;
      const currentMouseY = e.clientY;

      if (currentMouseY < this._lastMouseY && currentMouseY < topThreshold) {
        document.removeEventListener('mousemove', this._handleMouseMove);
        this.triggerPopup('exit_intent');
      }

      this._lastMouseY = currentMouseY;
    }

    _handleKeydown(e) {
      if (e.key === 'Escape' && this.modalBox.classList.contains('active')) {
        this.handlePopupClose();
      }
    }

    _handleModalClick(e) {
      if (e.target === this.modalBox) {
        this.handlePopupClose();
      }
    }

    _handleReminderClick() {
      this.showPopup();
    }

    _handleCookieConsent(event) {
      // Pandectes: consentType 'new' = visitor just decided; preferences bit 4 = targeting/marketing rejected
      const detail = (event && event.detail) || {};

      if (detail.consentType !== 'new') return;

      if ((Number(detail.preferences) & 4) === 0) {
        this.triggerPopup('cookie_consent');
      }
    }

    _handleCloseClick() {
      this.handlePopupClose();
    }

    init() {
      if (!this.state.subscribed) {
        this.showReminderButton();
      }

      this.setupReminderButton();
      this.setupCloseHandlers();

      if (window.Shopify && window.Shopify.designMode) {
        return;
      }

      if (this.state.triggered) {
        return;
      }

      if (this.state.dismissed) {
        // Closed recently without subscribing: only the reminder button, no automatic popup
        this.showReminderButton();

        return;
      }

      // Never compete with the cookie banner: wait until the visitor has decided (or no banner is shown)
      this.waitForConsentDecision(() => this.setupTriggers());
    }

    /* ---------- Cookie banner coordination ---------- */

    hasConsentCookie() {
      return document.cookie.split('; ').some((c) => c.indexOf(this.consent.cookieName + '=') === 0);
    }

    readConsentPreferences() {
      const raw = document.cookie.split('; ').find((c) => c.indexOf(this.consent.cookieName + '=') === 0);

      if (!raw) return null;

      try {
        const data = JSON.parse(atob(decodeURIComponent(raw.split('=')[1])));

        return typeof data.preferences === 'number' ? data.preferences : null;
      } catch (e) {
        return null;
      }
    }

    isBannerOpen() {
      const host = document.querySelector(this.consent.hostSelector);
      const dialog = host && host.shadowRoot ? host.shadowRoot.querySelector('dialog') : null;

      return !!(dialog && (dialog.open || dialog.getClientRects().length));
    }

    waitForConsentDecision(callback) {
      if (this._consentCallback) return;

      this._consentCallback = callback;

      if (this.hasConsentCookie()) {
        this.onConsentDecided(false);

        return;
      }

      const startedAt = Date.now();

      this._consentPollId = setInterval(() => {
        if (this.hasConsentCookie()) {
          this.onConsentDecided(true);

          return;
        }

        const elapsed = Date.now() - startedAt;
        const cmpPresent = !!document.querySelector(this.consent.hostSelector);

        if (cmpPresent && !this.isBannerOpen() && elapsed >= this.consent.noBannerGraceMs) {
          this.onConsentDecided(false);
        } else if (!cmpPresent && elapsed >= this.consent.noCmpTimeoutMs) {
          this.onConsentDecided(false);
        }
      }, this.consent.pollIntervalMs);
    }

    onConsentDecided(decidedByVisitorNow) {
      if (!this._consentCallback) return;

      const callback = this._consentCallback;

      this._consentDecidedByVisitorNow = decidedByVisitorNow === true;
      this._consentCallback = null;
      this.stopConsentWatcher();
      callback();
    }

    stopConsentWatcher() {
      if (this._consentPollId) {
        clearInterval(this._consentPollId);
        this._consentPollId = null;
      }
    }

    isDismissedRecently() {
      const at = parseInt(localStorage.getItem(this.storageKeys.dismissedAt), 10);

      if (!isNaN(at)) {
        return Date.now() - at < this.dismissCooldownMs;
      }

      // Legacy flag without timestamp: start the cooldown now
      if (localStorage.getItem(this.storageKeys.dismissed) === 'true') {
        localStorage.setItem(this.storageKeys.dismissedAt, String(Date.now()));

        return true;
      }

      return false;
    }

    setupTriggers() {
      const {
        triggerOnCookieAccept = true,
        scrollTriggerPercent = 35,
        timeTriggerSeconds = 30,
        triggerOnExitIntent = true
      } = this.options;

      if (triggerOnCookieAccept) {
        this.setupCookieConsentTrigger();

        const prefs = this.readConsentPreferences();

        if (prefs !== null && (prefs & 4) === 0 && this._consentDecidedByVisitorNow) {
          this.triggerPopup('cookie_consent');

          return;
        }
      }

      if (scrollTriggerPercent > 0) {
        this.setupScrollTrigger(scrollTriggerPercent);
      }

      if (timeTriggerSeconds > 0) {
        this.setupTimeTrigger(timeTriggerSeconds);
      }

      if (triggerOnExitIntent) {
        this.setupExitIntentTrigger();
      }
    }

    setupCookieConsentTrigger() {
      window.addEventListener('PandectesEvent_OnConsent', this._handleCookieConsent);
      document.addEventListener('PandectesEvent_OnConsent', this._handleCookieConsent);
    }

    setupScrollTrigger(percent) {
      this._scrollTriggerPercent = percent;
      window.addEventListener('scroll', this._handleScroll, { passive: true });
    }

    setupTimeTrigger(seconds) {
      this._timeoutId = setTimeout(() => {
        this.triggerPopup('time_elapsed');
      }, seconds * 1000);
    }

    setupExitIntentTrigger() {
      this._lastMouseY = 0;
      document.addEventListener('mousemove', this._handleMouseMove);
    }

    triggerPopup(source) {
      if (this.state.triggered || this.state.subscribed) {
        return;
      }

      this.state.triggered = true;
      sessionStorage.setItem(this.storageKeys.triggered, 'true');

      if (this._timeoutId) {
        clearTimeout(this._timeoutId);
        this._timeoutId = null;
      }

      this.showPopup();
    }

    showPopup() {
      this.hideReminderButton();
      this.hideRewardsButton();

      if (this.modalBox && typeof this.modalBox.show === 'function') {
        this.modalBox.setAttribute('style', '');
        this.modalBox.show();
      }
    }

    setupCloseHandlers() {
      const closeButtons = this.querySelectorAll(this.selectors.closeButton);
      
      closeButtons.forEach(btn => {
        btn.addEventListener('click', this._handleCloseClick);
      });

      document.addEventListener('keydown', this._handleKeydown);
      this.modalBox.addEventListener('click', this._handleModalClick);
    }

    handlePopupClose() {
      this.hidePopup();

      if (!this.state.subscribed) {
        this.state.dismissed = true;
        localStorage.setItem(this.storageKeys.dismissed, 'true');
        localStorage.setItem(this.storageKeys.dismissedAt, String(Date.now()));
        this.showReminderButton();
      }
    }

    hidePopup() {
      this.showRewardsButton();

      if (this.modalBox && typeof this.modalBox.hide === 'function') {
        this.modalBox.hide();
      }
    }

    setupReminderButton() {
      if (!this.reminderButton) return;
      
      this.reminderButton.addEventListener('click', this._handleReminderClick);
    }

    showReminderButton() {
      if (this.reminderButton && !this.state.subscribed) {
        this.reminderButton.classList.add('visible');
        this.reminderButton.setAttribute('aria-hidden', 'false');
      }
    }

    hideReminderButton() {
      if (this.reminderButton) {
        this.reminderButton.classList.remove('visible');
        this.reminderButton.setAttribute('aria-hidden', 'true');
      }
    }

    showRewardsButton() {
      const rewardsButton = document.querySelector(this.selectors.rewardsButton);
      
      if (rewardsButton) rewardsButton.style.display = 'block';
    }

    hideRewardsButton() {
      const rewardsButton = document.querySelector(this.selectors.rewardsButton);
      
      if (rewardsButton) rewardsButton.style.display = 'none';
    }

    handleSuccessfulSubscription() {
      this.state.subscribed = true;
      localStorage.setItem(this.storageKeys.subscribed, 'true');
      localStorage.removeItem(this.storageKeys.dismissed);
      localStorage.removeItem(this.storageKeys.dismissedAt);

      this.hideReminderButton();

      const hasCustomerPosted = document.location.search.includes('customer_posted');
      const hasNewsletterHash = document.location.hash.includes('popup-newsletter-signup');

      if (hasCustomerPosted && hasNewsletterHash && !this.state.successShown) {
        this.showPopup();
        this.state.successShown = true;
        localStorage.setItem(this.storageKeys.successShown, 'true');
      }
    }
  }

  if (typeof customElements.get("newsletter-popup") == "undefined") {
    customElements.define("newsletter-popup", NewsletterPopupController);
  }
}